/**
 * File Processing Utilities for CaseDiscovery File Upload
 * Handles file validation, base64 conversion, and type checking
 */

import {
  CHAT_ATTACHMENT_LIMITS,
  describeChatAttachmentCountLimit,
  formatChatAttachmentSize,
  formatChatAttachmentSizeRoundedUp,
} from "@hector/shared-types";
import type { FileData } from "@/types/chat";

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

// Supported file types
// `heic`/`heif` sont acceptés au dépôt mais n'atteignent jamais le backend : chaque
// chemin d'upload les convertit d'abord en JPEG (cf. utils/heicConversion.ts).
export const SUPPORTED_FILE_TYPES = [
  'pdf', 'doc', 'docx', 'odt', 'rtf', 'txt',
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'heif', 'eml', 'msg',
  // Spreadsheets
  'csv', 'xls', 'xlsx',
  // Audio formats
  'mp3', 'm4a', 'wav', 'ogg', 'opus', 'flac', 'webm'
] as const;

export type SupportedFileType = typeof SUPPORTED_FILE_TYPES[number];

/**
 * Get file type from filename
 */
export function getFileType(filename: string): string {
  const extension = filename.split('.').pop()?.toLowerCase().trim() || '';
  return extension;
}

/** Extensions that DocumentConverterService can convert to PDF server-side */
export const CONVERTIBLE_EXTENSIONS: ReadonlySet<string> = new Set([
  'docx', 'doc', 'odt', 'rtf', 'txt',
  'xlsx', 'xls', 'ods', 'csv',
  'pptx', 'ppt', 'odp',
  'png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'tif',
  'eml', 'msg',
]);

/** Check if a file can be converted to PDF (non-PDF and in the supported list) */
export function isConvertibleToPdf(filePathOrName: string): boolean {
  const ext = filePathOrName.split('.').pop()?.toLowerCase() ?? '';
  return ext !== 'pdf' && CONVERTIBLE_EXTENSIONS.has(ext);
}

/**
 * Check if file type is supported
 */
export function isSupportedFileType(fileType: string): fileType is SupportedFileType {
  return SUPPORTED_FILE_TYPES.includes(fileType as SupportedFileType);
}

/**
 * Get MIME type from file extension
 */
export function getMimeType(fileType: string): string {
  const mimeTypes: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    odt: 'application/vnd.oasis.opendocument.text',
    rtf: 'application/rtf',
    txt: 'text/plain',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
    eml: 'message/rfc822',
    msg: 'application/vnd.ms-outlook',
    // Spreadsheet formats — backend extrait le contenu via exceljs (parse natif, services/document-extraction/native/)
    csv: 'text/csv',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    // Audio formats
    mp3: 'audio/mpeg',
    m4a: 'audio/mp4',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    opus: 'audio/opus',
    flac: 'audio/flac',
    webm: 'audio/webm'
  };
  return mimeTypes[fileType] || '';
}

/**
 * Validate file before upload
 */
export function validateFile(file: File): FileValidationResult {
  const fileType = getFileType(file.name);
  
  // Check file type
  if (!isSupportedFileType(fileType)) {
    return {
      isValid: false,
      error: `Type de fichier non supporté : ${fileType}. Types autorisés : ${SUPPORTED_FILE_TYPES.join(', ')}`
    };
  }
  
  // Check file size — même plafond pour tous les formats, lu dans la constante
  // partagée avec le serveur (`CHAT_ATTACHMENT_LIMITS`, #2066).
  const maxSize = CHAT_ATTACHMENT_LIMITS.maxFileSizeBytes;
  if (file.size > maxSize) {
    return {
      isValid: false,
      // Poids arrondi VERS LE HAUT : un octet de trop s'écrirait sinon « 50 Mo »
      // à côté de « Maximum autorisé : 50 Mo ».
      error: `Fichier trop volumineux : ${formatChatAttachmentSizeRoundedUp(file.size)}. `
        + `Maximum autorisé : ${formatChatAttachmentSize(maxSize)}.`
    };
  }
  
  return { isValid: true };
}

/** Ce que le composeur porte déjà au moment où l'on valide un ajout ou un envoi. */
export interface ComposerAttachmentContext {
  /** Fichiers déjà joints au message. Seul leur nombre est lu. */
  existingFiles?: ReadonlyArray<File>;
  /**
   * Fichiers déposés dont la conversion (photo iPhone → JPEG) tourne encore :
   * absents de la liste, ils y occupent déjà leur place.
   */
  pendingCount?: number;
}

/**
 * Valide les pièces jointes d'un MESSAGE : nombre, puis format et poids de
 * chaque nouveau fichier.
 *
 * Le plafond de nombre est CUMULATIF (#2066) : il porte sur le message entier
 * — fichiers déjà joints compris — et non sur le lot que l'avocat vient
 * d'ajouter. Aucun plafond de poids total (décision du 2026-10-15) : le serveur
 * ne l'a jamais appliqué, et les add-ins Word et Outlook n'en ont pas.
 *
 * Appelée à l'ajout, et à l'envoi avec `newFiles = []` : cette seconde lecture
 * fait autorité, elle voit aussi les fichiers posés par l'hôte.
 *
 * @param newFiles Fichiers que l'avocat ajoute maintenant (vide à l'envoi).
 * @param context Ce que le composeur porte déjà.
 */
export function validateMultipleFiles(
  newFiles: File[],
  context: ComposerAttachmentContext = {},
): FileValidationResult {
  const existingFiles = context.existingFiles ?? [];

  // Nombre de documents du message
  const totalCount = existingFiles.length + (context.pendingCount ?? 0) + newFiles.length;
  if (totalCount > CHAT_ATTACHMENT_LIMITS.maxFilesPerMessage) {
    return { isValid: false, error: describeChatAttachmentCountLimit() };
  }

  // Chaque nouveau fichier (les fichiers déjà joints ont été validés à leur ajout)
  for (const file of newFiles) {
    const validation = validateFile(file);
    if (!validation.isValid) {
      return {
        isValid: false,
        error: `${file.name} — ${validation.error}`
      };
    }
  }

  return { isValid: true };
}

/**
 * Convert file to base64
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      try {
        // Remove data URL prefix (data:application/pdf;base64,)
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        if (!base64) {
          throw new Error('Failed to extract base64 content');
        }
        resolve(base64);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = (error) => reject(error);
  });
}

/**
 * Process file for upload - convert to FileData format
 */
export async function processFileForUpload(file: File): Promise<FileData> {
  // Validate file first
  const validation = validateFile(file);
  if (!validation.isValid) {
    throw new Error(validation.error);
  }
  
  // Convert to base64
  const content = await fileToBase64(file);
  const fileType = getFileType(file.name) as SupportedFileType;
  
  return {
    content,
    filename: file.name,
    file_type: fileType
  };
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Get file icon based on file type
 */
export function getFileIconClass(fileType: string): string {
  const iconClasses: Record<string, string> = {
    pdf: 'text-red-400',
    doc: 'text-accent-blue/70',
    docx: 'text-accent-blue/70',
    png: 'text-violet-400',
    jpg: 'text-violet-400',
    jpeg: 'text-violet-400',
    gif: 'text-violet-400',
    webp: 'text-violet-400',
    eml: 'text-teal-500',
    msg: 'text-teal-500',
    csv: 'text-emerald-400',
    xls: 'text-emerald-400',
    xlsx: 'text-emerald-400',
    mp3: 'text-amber-400',
    m4a: 'text-amber-400',
    wav: 'text-amber-400',
    ogg: 'text-amber-400',
    opus: 'text-amber-400',
    flac: 'text-amber-400',
    webm: 'text-amber-400'
  };
  return iconClasses[fileType] || 'text-grey-400';
}

/**
 * Check if file type is an image
 */
export function isImageFile(fileType: string): boolean {
  const imageTypes = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
  return imageTypes.includes(fileType.toLowerCase());
}

/**
 * Check if file type is an audio file
 */
export function isAudioFile(fileType: string): boolean {
  const audioTypes = ['mp3', 'm4a', 'wav', 'ogg', 'opus', 'flac', 'webm'];
  return audioTypes.includes(fileType.toLowerCase());
}

/**
 * Generate preview URL for image files
 */
export function createImagePreview(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!isImageFile(getFileType(file.name))) {
      reject(new Error('File is not an image'));
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(e.target?.result as string);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
